import { useEffect, useMemo, useState } from 'react';

import { useLocale } from 'next-intl';
import { useLatest } from 'react-use';

import { useConfigValue } from '@/components/providers/config-provider';
import { placeNameLocale } from '@/i18n/place-name-locale';
import { readJsonFromLocalStorage, writeJsonToLocalStorage } from '@/lib/storage/safe-storage';
import { post } from '@/lib/utils/api-client';

type DestinationsMap = Record<string, string>;

export type GetDestinationName = (code: string) => string;

// Stable empty fallback for `cache.get(locale)` misses. Returning `{}` inline
// would allocate a new object every render, defeating downstream memoization
// (e.g. ActivityTrail passes `destinations` into `useStepRecords` deps —
// without a stable ref it re-runs on every tick during the pre-resolve window).
const EMPTY_DESTINATIONS_MAP: DestinationsMap = Object.freeze({}) as DestinationsMap;

const BATCH_DELAY = 50;
const MAX_CACHE_ENTRIES = 100;
const STORAGE_KEY_PREFIX = 'destinations-cache-';
// 2: Spanish entries stored before Spanish place names existed hold English ones.
const STORAGE_VERSION = 2;

interface StoredDestinations {
  version: number;
  data: DestinationsMap;
}

const cache = new Map<string, DestinationsMap>();
const fetched = new Map<string, Set<string>>();
const pending = new Map<string, Set<string>>();
const subscribers = new Map<string, Set<() => void>>();
const timers = new Map<string, NodeJS.Timeout>();
const initialized = new Set<string>();

/**
 * Module-level API base URL. When set, `executeBatch` prepends this to the
 * `/api/destinations` path for cross-origin requests (external npm consumers).
 * Set via useEffect in `useDestinations` — safe because assignment occurs
 * after render and is idempotent.
 */
let _apiBaseUrl: string | null = null;

function getStorageKey(locale: string): string {
  return `${STORAGE_KEY_PREFIX}${locale}`;
}

function loadFromStorage(locale: string): DestinationsMap {
  const stored = readJsonFromLocalStorage<StoredDestinations | null>({
    key: getStorageKey(locale),
    fallback: null,
  });
  if (!stored || stored.version !== STORAGE_VERSION) return {};
  return stored.data ?? {};
}

function saveToStorage(locale: string, data: DestinationsMap): void {
  writeJsonToLocalStorage<StoredDestinations>({
    key: getStorageKey(locale),
    value: { version: STORAGE_VERSION, data },
  });
}

function initializeCache(locale: string): void {
  if (initialized.has(locale)) return;
  initialized.add(locale);

  const stored = loadFromStorage(locale);
  if (Object.keys(stored).length > 0) {
    const existing = cache.get(locale) ?? {};
    cache.set(locale, { ...stored, ...existing });
    const fetchedSet = getOrCreateSet(fetched, locale);
    Object.keys(stored).forEach(code => fetchedSet.add(code));
  }
}

export function resetDestinationsCache() {
  cache.clear();
  fetched.clear();
  pending.clear();
  timers.forEach(timer => clearTimeout(timer));
  timers.clear();
  subscribers.clear();
  initialized.clear();
  _apiBaseUrl = null;
}

/**
 * Pre-populate the destination cache for a given locale.
 * Useful in storybook or tests where the /api/destinations endpoint is unavailable.
 * @note Client-side only. Do not call during SSR.
 */
export function seedDestinationsCache(locale: string, destinations: DestinationsMap) {
  const existing = cache.get(locale) ?? {};
  cache.set(locale, { ...existing, ...destinations });
  const fetchedSet = getOrCreateSet(fetched, locale);
  Object.keys(destinations).forEach(code => fetchedSet.add(code));
  initialized.add(locale);
}

function getOrCreateSet<K, V>(map: Map<K, Set<V>>, key: K): Set<V> {
  let set = map.get(key);
  if (!set) {
    set = new Set();
    map.set(key, set);
  }
  return set;
}

function executeBatch(locale: string) {
  const pendingSet = getOrCreateSet(pending, locale);
  if (!pendingSet.size) return;

  const codesToFetch = [...pendingSet];
  pendingSet.clear();

  post<DestinationsMap>(`${_apiBaseUrl ?? ''}/api/destinations`, { codes: codesToFetch, locale })
    .then(response => {
      const merged = { ...cache.get(locale), ...response };
      const entries = Object.entries(merged);

      const trimmedCache =
        entries.length > MAX_CACHE_ENTRIES
          ? Object.fromEntries(entries.slice(-MAX_CACHE_ENTRIES))
          : merged;

      cache.set(locale, trimmedCache);
      saveToStorage(locale, trimmedCache);

      codesToFetch.forEach(code => getOrCreateSet(fetched, locale).add(code));
      subscribers.get(locale)?.forEach(cb => cb());
    })
    .catch(() => {
      codesToFetch.forEach(code => pendingSet.add(code));
    });
}

function scheduleBatch(locale: string) {
  if (timers.has(locale)) return;

  timers.set(
    locale,
    setTimeout(() => {
      timers.delete(locale);
      executeBatch(locale);
    }, BATCH_DELAY),
  );
}

function requestCodes(codes: string[], locale: string) {
  const cachedMap = cache.get(locale) ?? {};
  const fetchedSet = fetched.get(locale);
  const pendingSet = getOrCreateSet(pending, locale);

  const missing = codes.filter(
    code => !(code in cachedMap) && !fetchedSet?.has(code) && !pendingSet.has(code),
  );
  if (!missing.length) return;

  missing.forEach(code => pendingSet.add(code));
  scheduleBatch(locale);
}

export function useDestinations(codes: string[] = []) {
  const pageLocale = useLocale();
  const apiBaseUrl = useConfigValue<string>('apiBaseUrl');
  const placeNameLocales = useConfigValue<readonly string[]>('meta.placeNameLocales');
  const locale = placeNameLocale({ locale: pageLocale, placeNameLocales });

  initializeCache(locale);

  const codesRef = useLatest(codes);
  const [, forceUpdate] = useState(0);

  // Sync the module-level base URL so executeBatch uses it for cross-origin requests
  useEffect(() => {
    if (apiBaseUrl) _apiBaseUrl = apiBaseUrl;
  }, [apiBaseUrl]);

  useEffect(() => {
    const subs = getOrCreateSet(subscribers, locale);
    const callback = () => forceUpdate(n => n + 1);
    subs.add(callback);
    return () => {
      subs.delete(callback);
    };
  }, [locale]);

  const codesKey = useMemo(() => [...codes].sort().join(','), [codes]);

  useEffect(() => {
    if (codesRef.current.length) {
      requestCodes(codesRef.current, locale);
    }
  }, [codesKey, locale, codesRef]);

  const destinations = cache.get(locale) ?? EMPTY_DESTINATIONS_MAP;
  const fetchedSet = fetched.get(locale);

  const getDestinationName = (code: string) => destinations[code] || code;

  const isLoading = codes.length > 0 && codes.some(code => !fetchedSet?.has(code));

  return { getDestinationName, destinations, isLoading };
}
